'use client'

import { Dialog } from '@headlessui/react'
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { CarouselContent } from '@src/components/Carousel/CarouselContent'
import type { CarouselProps } from '@src/components/Carousel/CarouselProps'
import { useCarouselHandlers } from '@src/components/Carousel/useCarouselHandlers'
import { DesktopThumbnails } from '@src/components/ProductConfigurator/modules/ProductGalleryV2/DesktopThumbnails'
import { ProductGalleryModalContent } from '@src/components/ProductConfigurator/modules/ProductGalleryV2/ProductGalleryModalContent'

export const DesktopCarousel = ({
  images,
  setIsModalOpen,
  isModalOpen,
}: {
  images: {
    url: string
    alt: string
  }[]
  setIsModalOpen: (value: boolean) => void
  isModalOpen: boolean
}) => {
  const carouselProps: CarouselProps = {
    slides: images.map((image, index) => (
      <button
        className="group relative flex cursor-pointer justify-center"
        key={`${image.url}-${index}`}
        onClick={() => {
          setIsModalOpen(true)
        }}
      >
        <Image
          src={image.url}
          alt={image.alt}
          width={662}
          height={662}
          className="max-w-full rounded-3xl object-contain"
        />
        <div className="absolute right-4 top-4 flex size-10 rotate-45 flex-col items-center justify-center gap-0.5 rounded-full bg-white opacity-0 shadow-md transition-opacity *:stroke-[2px] lg:group-hover:opacity-100">
          <ArrowUpIcon className="size-3" />
          <ArrowDownIcon className="size-3" />
        </div>
      </button>
    )),
    builderBlock: {},
    attributes: [],
    alignmentMobile: 'center',
    showBottomArrowsDesktop: true,
    showArrows: false,
    overflowMobile: false,
    showDotsOnMobile: false,
    showDots: false,
    slidesPerView: 1,
    numberOfSlidesMoving: 1,
  }

  const carouselHandlers = useCarouselHandlers(carouselProps)

  const [currentSlide, setCurrentSlide] = useState(0)

  const nextSlide = () => {
    carouselHandlers.nextSlide()
    setCurrentSlide((currentSlide + 1) % images.length)
  }

  const previousSlide = () => {
    carouselHandlers.previousSlide()
    setCurrentSlide((currentSlide - 1 + images.length) % images.length)
  }

  const setSlide = (slide: number) => {
    setCurrentSlide(slide)
    carouselHandlers.instanceRef.current?.moveToIdx(slide)
  }

  // handle currentSlide overflow on images length change
  useEffect(() => {
    const newIndex = Math.min(currentSlide, images.length - 1)
    setCurrentSlide(newIndex)
    setTimeout(() => carouselHandlers.instanceRef.current?.moveToIdx(newIndex), 100)
  }, [currentSlide, images.length, carouselHandlers.instanceRef])

  const containerRef = useRef<HTMLDivElement>(null)

  const height = containerRef.current?.clientWidth

  return (
    <>
      <div key={`slides-${images.length}`} className="sticky top-24 mb-6 hidden lg:flex lg:gap-12">
        <DesktopThumbnails
          height={height}
          images={images}
          currentSlide={currentSlide}
          setCurrentSlide={setSlide}
        />

        <div
          ref={containerRef}
          className="relative mt-20 hidden aspect-[300/350] min-h-[376px] max-w-full flex-1 overflow-hidden lg:block"
        >
          <div className="absolute inset-0">
            <CarouselContent
              {...carouselProps}
              {...carouselHandlers}
              nextSlide={nextSlide}
              previousSlide={previousSlide}
            />
          </div>
        </div>
      </div>

      <Dialog
        transition
        open={isModalOpen}
        className="relative z-50 transition duration-200 ease-out data-[closed]:opacity-0 data-[leave]:opacity-0"
        onClose={() => {
          setIsModalOpen(false)
        }}
      >
        <div className="fixed inset-0 flex h-screen w-screen items-center justify-center bg-bodi-white">
          <ProductGalleryModalContent
            images={images}
            currentSlide={carouselHandlers.currentSlide}
            nextSlide={nextSlide}
            previousSlide={previousSlide}
            setCurrentSlide={setSlide}
          />
        </div>
      </Dialog>
    </>
  )
}
